import { useEffect, useMemo, useState } from "react";
import type { AdminEvent, VolunteerRole } from "../../../shared/types";
import { ROLE_LABELS, GRADE_OPTIONS } from "../../../shared/types";
import {
  adminDeleteVolunteer,
  adminListEvents,
  adminListVolunteers,
  adminUpdateVolunteer,
  type AdminVolunteerListItem,
} from "../../api";
import { formatDate, monthLabel, weekdayOf } from "../../lib/hours";

export default function VolunteersTab() {
  const [rows, setRows] = useState<AdminVolunteerListItem[]>([]);
  const [events, setEvents] = useState<AdminEvent[]>([]);
  const [minHours, setMinHours] = useState(8);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");
  const [hours, setHours] = useState("");

  const [editing, setEditing] = useState<AdminVolunteerListItem | null>(null);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const params: Record<string, string> = {};
      if (q.trim()) params.q = q.trim();
      if (role) params.role = role;
      if (status) params.status = status;
      if (hours) params.hours = hours;
      const [v, e] = await Promise.all([adminListVolunteers(params), adminListEvents()]);
      setRows(v.volunteers);
      setMinHours(v.minHours);
      setEvents(e.events);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : "載入失敗");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, status, hours]);

  return (
    <div>
      <div className="toolbar">
        <input
          placeholder="搜尋姓名 / 系所 / Email"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
        />
        <button className="btn ghost sm" onClick={load}>
          搜尋
        </button>
        <button className="btn ghost sm" onClick={load} title="重新抓取最新填單資料">
          ↻ 重新整理最新填單
        </button>
        <select value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="">全部身分</option>
          {(Object.keys(ROLE_LABELS) as VolunteerRole[]).map((r) => (
            <option key={r} value={r}>
              {ROLE_LABELS[r]}
            </option>
          ))}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">全部狀態</option>
          <option value="draft">草稿</option>
          <option value="submitted">已最終確認</option>
        </select>
        <select value={hours} onChange={(e) => setHours(e.target.value)}>
          <option value="">時數不拘</option>
          <option value="met">已達 {minHours} 小時</option>
          <option value="unmet">未達 {minHours} 小時</option>
        </select>
        <span className="muted">共 {rows.length} 筆</span>
      </div>

      {err && <div className="alert err">{err}</div>}
      {loading ? (
        <p>載入中…</p>
      ) : (
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>姓名</th>
                <th>系所 / 年級</th>
                <th>身分</th>
                <th>Email（主要）</th>
                <th>總時數</th>
                <th>狀態</th>
                <th>已選場次</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((v) => (
                <tr key={v.id}>
                  <td>{v.name}</td>
                  <td>
                    {v.department}
                    <br />
                    <span className="muted">{v.grade}</span>
                  </td>
                  <td>
                    {v.roles.map((r) => (
                      <span className="tag" key={r}>
                        {ROLE_LABELS[r]}
                      </span>
                    ))}
                  </td>
                  <td style={{ wordBreak: "break-all" }}>{v.primaryEmail}</td>
                  <td>
                    {v.totalHours}
                    {v.totalHours >= minHours ? (
                      <span className="tag ok">達標</span>
                    ) : (
                      <span className="tag warn">未達</span>
                    )}
                  </td>
                  <td>
                    {v.status === "submitted" ? (
                      <span className="tag ok">已確認</span>
                    ) : (
                      <span className="tag muted">草稿</span>
                    )}
                  </td>
                  <td>
                    {v.events.length} 場
                    {v.events.some((e) => e.waitlistPosition > 0) && (
                      <span className="tag warn">
                        遞補 {v.events.filter((e) => e.waitlistPosition > 0).length}
                      </span>
                    )}
                    <br />
                    <span className="muted" style={{ fontSize: 11 }}>
                      {v.events
                        .slice(0, 3)
                        .map(
                          (e) =>
                            (e.eventDate ? e.eventDate.slice(5) : "待定") +
                            (e.waitlistPosition > 0 ? `(遞${e.waitlistPosition})` : ""),
                        )
                        .join(", ")}
                      {v.events.length > 3 ? " …" : ""}
                    </span>
                  </td>
                  <td>
                    <button className="btn ghost sm" onClick={() => setEditing(v)}>
                      編輯
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="muted center">
                    沒有符合條件的資料
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditModal
          volunteer={editing}
          events={events}
          minHours={minHours}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function EditModal({
  volunteer,
  events,
  minHours,
  onClose,
  onSaved,
}: {
  volunteer: AdminVolunteerListItem;
  events: AdminEvent[];
  minHours: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(volunteer.name);
  const [department, setDepartment] = useState(volunteer.department);
  const [grade, setGrade] = useState(volunteer.grade);
  const [phone, setPhone] = useState(volunteer.phone);
  const [note, setNote] = useState(volunteer.note ?? "");
  const [email, setEmail] = useState(
    volunteer.primaryEmail || volunteer.personalEmail || volunteer.schoolEmail || "",
  );
  const [roles, setRoles] = useState<VolunteerRole[]>(volunteer.roles);
  const [selected, setSelected] = useState<Set<number>>(new Set(volunteer.eventIds));
  const [statusSubmitted, setStatusSubmitted] = useState(volunteer.status === "submitted");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const grouped = useMemo(() => {
    const map = new Map<string, AdminEvent[]>();
    for (const e of events) {
      if (!map.has(e.monthGroup)) map.set(e.monthGroup, []);
      map.get(e.monthGroup)!.push(e);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [events]);

  const totalHours = useMemo(
    () =>
      Math.round(
        events.filter((e) => selected.has(e.id)).reduce((s, e) => s + e.creditHours, 0) * 10,
      ) / 10,
    [events, selected],
  );

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      await adminUpdateVolunteer(volunteer.id, {
        name,
        department,
        grade,
        phone,
        note,
        email: email || undefined,
        roles,
        eventIds: [...selected],
        action: statusSubmitted ? "submit" : "draft",
        status: statusSubmitted ? "submitted" : "draft",
      });
      onSaved();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : "儲存失敗");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!confirm(`確定刪除「${volunteer.name}」的所有填寫資料？此動作無法復原。`)) return;
    setBusy(true);
    try {
      await adminDeleteVolunteer(volunteer.id);
      onSaved();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : "刪除失敗");
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>編輯志工資料 · #{volunteer.id}</h3>
        {err && <div className="alert err">{err}</div>}

        <div className="row2">
          <div className="field">
            <label>姓名</label>
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="field">
            <label>系所</label>
            <input value={department} onChange={(e) => setDepartment(e.target.value)} />
          </div>
        </div>
        <div className="row2">
          <div className="field">
            <label>年級</label>
            <input
              value={grade}
              onChange={(e) => setGrade(e.target.value)}
              list="grade-options"
              placeholder="大一 / 碩士一 / 其他：在職專班…"
            />
            <datalist id="grade-options">
              {GRADE_OPTIONS.map((g) => (
                <option key={g} value={g} />
              ))}
            </datalist>
          </div>
          <div className="field">
            <label>手機</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
        </div>
        <div className="field">
          <label>聯絡 Email</label>
          <input value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>

        <div className="field">
          <label>志工身分</label>
          <div className="checks">
            {(Object.keys(ROLE_LABELS) as VolunteerRole[]).map((r) => (
              <label key={r} className={`check${roles.includes(r) ? " on" : ""}`}>
                <input
                  type="checkbox"
                  checked={roles.includes(r)}
                  onChange={() =>
                    setRoles((rs) =>
                      rs.includes(r) ? rs.filter((x) => x !== r) : [...rs, r],
                    )
                  }
                />
                {ROLE_LABELS[r]}
              </label>
            ))}
          </div>
        </div>

        <div className="field">
          <label>備註</label>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} />
        </div>

        <div className="field">
          <label>
            勾選場次（合計 {totalHours} 小時
            {totalHours >= minHours ? "，已達標" : `，距 ${minHours} 小時還差 ${Math.round((minHours - totalHours) * 10) / 10}`}
            ）
          </label>
          <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid var(--line)", borderRadius: 10, padding: 8 }}>
            {grouped.map(([mg, list]) => (
              <div key={mg} style={{ marginBottom: 8 }}>
                <div className="muted" style={{ fontWeight: 700, margin: "4px 0" }}>
                  {monthLabel(mg)}
                </div>
                {list.map((e) => (
                  <label
                    key={e.id}
                    style={{ display: "flex", gap: 8, alignItems: "center", padding: "3px 0", fontSize: 13 }}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(e.id)}
                      onChange={() =>
                        setSelected((s) => {
                          const n = new Set(s);
                          n.has(e.id) ? n.delete(e.id) : n.add(e.id);
                          return n;
                        })
                      }
                    />
                    <span>
                      {formatDate(e.eventDate)}
                      {e.eventDate ? `（${weekdayOf(e.eventDate)}）` : ""} · {e.title} · {e.creditHours}h
                      {!e.isOpen && <span className="tag muted">已關閉</span>}
                    </span>
                  </label>
                ))}
              </div>
            ))}
          </div>
        </div>

        <label className="check" style={{ marginBottom: 12 }}>
          <input
            type="checkbox"
            checked={statusSubmitted}
            onChange={(e) => setStatusSubmitted(e.target.checked)}
          />
          標記為「已最終確認」（管理員可不受 {minHours} 小時限制）
        </label>

        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn primary" style={{ flex: 1 }} disabled={busy} onClick={save}>
            {busy ? "儲存中…" : "儲存"}
          </button>
          <button className="btn ghost" disabled={busy} onClick={onClose}>
            取消
          </button>
          <button className="btn danger" disabled={busy} onClick={remove}>
            刪除
          </button>
        </div>
      </div>
    </div>
  );
}
